import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import LightGestion from 'components/headers/lightGestion';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import { Button, Modal, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const Clients = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'debtors'
  const [showAdd, setShowAdd] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', company: '', phone: '', ice: '', address: '', credit: 0, status: 'active' });
  const [showEdit, setShowEdit] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const fetchClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get('https://plateformewmg.cloud/api/Gestion/clientgestions', { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      // backend may return { data: [...] } or an array directly
      const raw = Array.isArray(res.data) ? res.data : (res.data.data || res.data.clients || []);
      // Normalize different backend shapes to UI-friendly keys
      const data = (raw || []).map(r => ({
        id: r.id || r.client_id || r._id || r.ID,
        name: r.name || r.Nom || r.nom || '',
        company: r.Entreprise || r.entreprise || r.company || r.Company || r.raison_sociale || '',
        phone: r.phone || r.Telephone || r.telephone || r.Phone || '',
        ice: r.ice || r.ICE || r.Ice || '',
        address: r.address || r.Address || r.adresse || '',
        credit: (r.credit !== undefined && r.credit !== null) ? Number(r.credit) : (r.Credit !== undefined ? Number(r.Credit) : 0),
        status: r.status || r.Status || 'active',
        // keep raw for debugging if needed
        _raw: r
      }));
      setClients(data);
    } catch (e) {
      console.error(e);
      Swal.fire(t('clients.error', 'Erreur'), t('clients.fetchFailed', 'Impossible de récupérer les clients'));
    }
  }, [t]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const totals = {
    total: clients.length,
    // debtors: count clients that have any credit (non-zero)
    debtors: clients.filter(c => Number(c.credit || 0) !== 0).length,
    totalCredit: clients.reduce((s,c) => s + (Number(c.credit || 0)), 0),
    healthy: clients.filter(c => Number(c.credit || 0) === 0).length,
  };

  const visible = clients.filter(c => {
  const q = search.trim().toLowerCase();
  const matchesSearch = !q || (c.name || '').toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q) || (c.phone || '').toLowerCase().includes(q) || (c.ice || '').toLowerCase().includes(q);
    if (!matchesSearch) return false;
    if (filter === 'debtors') return Number(c.credit || 0) !== 0;
    return true;
  });

  const handleAdd = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Map frontend fields to backend-validated keys
      if (!newClient.name || newClient.name.trim() === '') {
        Swal.fire(t('clients.validation', 'Validation'), t('clients.nameRequired', 'Le nom du client est requis.'), 'warning');
        return;
      }
      const payload = {
        Nom: newClient.name,
        Entreprise: newClient.company || null,
        ICE: newClient.ice || null,
        Telephone: newClient.phone || null,
        Address: newClient.address || null,
        Credit: Number(newClient.credit || 0),
        status: newClient.status || 'active',
      };

      await axios.post('https://plateformewmg.cloud/api/Gestion/clientgestions', payload, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      Swal.fire(t('clients.success', 'Succès'), t('clients.added', 'Client ajouté'), 'success');
      setShowAdd(false);
  setNewClient({ name: '', company: '', phone: '', ice: '', address: '' });
      fetchClients();
    } catch (e) {
      console.error(e);
      Swal.fire(t('clients.error', 'Erreur'), JSON.stringify(e?.response?.data || e.message));
    }
  };

  const handleEdit = (client) => {
    // open edit modal and populate editing state
    setEditingClient({ ...client });
    setShowEdit(true);
  };

  const handleUpdate = async () => {
    if (!editingClient) return;
    try {
      const token = localStorage.getItem('authToken');
      const id = editingClient.id || editingClient.client_id || editingClient._raw?.id;
      if (!editingClient.name || editingClient.name.trim() === '') {
        Swal.fire(t('clients.validation', 'Validation'), t('clients.nameRequired', 'Le nom du client est requis.'), 'warning');
        return;
      }
      const payload = {
        Nom: editingClient.name,
        Entreprise: editingClient.company || null,
        ICE: editingClient.ice || null,
        Telephone: editingClient.phone || null,
        Address: editingClient.address || null,
        Credit: Number(editingClient.credit || 0),
        status: editingClient.status || 'active',
      };

      // Try PUT first, fallback to POST with _method=PUT if server expects that
      try {
        await axios.put(`https://plateformewmg.cloud/api/admin/clientgestions/${id}`, payload, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      } catch (e) {
        // fallback
        await axios.post(`https://plateformewmg.cloud/api/admin/clientgestions/${id}`, { ...payload, _method: 'PUT' }, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      }

      Swal.fire(t('clients.success', 'Succès'), t('clients.updated', 'Client mis à jour'), 'success');
      setShowEdit(false);
      setEditingClient(null);
      fetchClients();
    } catch (e) {
      console.error(e);
      Swal.fire(t('clients.error', 'Erreur'), JSON.stringify(e?.response?.data || e.message));
    }
  };

  // Display helper: show dashes when value is null/empty
  const show = (v) => {
    if (v === null || v === undefined) return '----';
    const s = String(v).trim();
    return s === '' ? '----' : s;
  };

  return (
    <AnimationRevealPage>
      <LightGestion />
      <div className="container  app-content" style={{ maxWidth: 1100 ,marginTop:"-60px",marginLeft:"-20px"}}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontWeight: 900, fontSize: '30px', marginBottom: 6 }}>{t('clients.title', 'Gestion des Clients')}</h2>
          <Button onClick={() => setShowAdd(true)}>{t('clients.addClient', 'Ajouter un Client')}</Button>
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('clients.searchPlaceholder', 'Rechercher par nom, téléphone ou ICE...')} style={{ flex: 1, padding: 16, borderRadius: 8, border: '1px solid #e6e6e9' }} />
            <div style={{ display: 'flex', gap: 12 }}>
              <Button variant={filter === 'debtors' ? 'warning' : 'outline-warning'} onClick={() => setFilter('debtors')}>{t('clients.filters.debtors', 'Débiteurs')}</Button>
              <Button variant={filter === 'all' ? 'primary' : 'outline-secondary'} onClick={() => setFilter('all')}>{t('clients.filters.all', 'Tout')}</Button>
          </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 20 }}>
            <div style={{ background: 'var(--card-bg)', padding: 18, borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.04)', border: '1px solid var(--border)' }}>
              <div style={{ color: '#6b7280' }}>{t('clients.stats.total', 'Total Clients')}</div>
              <div style={{ fontWeight: 800, marginTop: 8 }}>{totals.total}</div>
            </div>
            <div style={{ background: 'var(--card-bg)', padding: 18, borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.04)', border: '1px solid var(--border)' }}>
              <div style={{ color: '#6b7280' }}>{t('clients.stats.debtors', 'Débiteurs')}</div>
              <div style={{ fontWeight: 800, marginTop: 8 }}>{totals.debtors}</div>
            </div>
            <div style={{ background: 'var(--card-bg)', padding: 18, borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.04)', border: '1px solid var(--border)' }}>
              <div style={{ color: '#6b7280' }}>{t('clients.stats.totalCredit', 'Crédit Total')}</div>
              <div style={{ fontWeight: 800, marginTop: 8 }}>{totals.totalCredit.toFixed(2)} {t('stock.currency', 'MAD')}</div>
            </div>
            <div style={{ background: 'var(--card-bg)', padding: 18, borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.04)', border: '1px solid var(--border)' }}>
              <div style={{ color: '#6b7280' }}>{t('clients.stats.healthy', 'Clients Sains')}</div>
              <div style={{ fontWeight: 800, marginTop: 8 }}>{totals.healthy}</div>
            </div>
          </div>

          <div style={{ marginTop: 20, background: 'var(--card-bg)', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 120px 1fr 160px 160px', gap: 12, padding: 18, background: 'var(--bg)', color: '#6b7280', fontWeight: 700 }}>
              <div>{t('clients.headers.name', 'NOM')}</div>
              <div>{t('clients.headers.phone', 'TÉLÉPHONE')}</div>
              <div>{t('clients.headers.ice', 'ICE')}</div>
              <div>{t('clients.headers.company', 'ENTREPRISE')}</div>
              <div>{t('clients.headers.address', 'ADRESSE')}</div>
              <div>{t('clients.headers.credit', 'CRÉDIT')}</div>
              <div>{t('clients.headers.actions', 'ACTIONS')}</div>
            </div>

            <div>
              {visible.map((c, idx) => (
                <div key={c.id || c.client_id || idx} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 120px 1fr 160px 160px', gap: 12, padding: 18, alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 22, background: '#6b8cff', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{(c.name || ' ')[0]?.toUpperCase() || 'U'}</div>
                    <div>
                      <div style={{ fontWeight: 700 }}>{show(c.name)}</div>
                      <div style={{ color: '#10b981' }}>{(Number(c.credit || 0) >= 0) ? t('clients.status.ok', 'À jour') : t('clients.status.debtor', 'Débiteur')}</div>
                    </div>
                  </div>
                  <div style={{ color: '#2563eb' }}>{show(c.phone)}</div>
                  <div style={{ color: '#9ca3af' }}>{show(c.ice)}</div>
                  <div style={{ color: '#9ca3af' }}>{show(c.company)}</div>
                  <div style={{ color: '#374151' }}>{show(c.address)}</div>
                  <div style={{ textAlign: 'center', fontWeight: 700, color: Number(c.credit || 0) >= 0 ? '#10b981' : '#ef4444' }}>{Number(c.credit || 0).toFixed(2)} {t('stock.currency', 'MAD')}</div>
                  <div style={{ textAlign: 'center' }}>
                    <Button variant="outline-primary" onClick={() => handleEdit(c)}>{t('clients.modify', 'Modifier')}</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>


      <Modal show={showAdd} onHide={() => setShowAdd(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('clients.addClient', 'Ajouter un Client')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>{t('clients.labels.name', 'Nom du client')}</Form.Label>
              <Form.Control required value={newClient.name} onChange={(e) => setNewClient({...newClient, name: e.target.value})} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('clients.labels.company', 'Entreprise')}</Form.Label>
              <Form.Control value={newClient.company} onChange={(e) => setNewClient({...newClient, company: e.target.value})} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('clients.labels.phone', 'Téléphone')}</Form.Label>
              <Form.Control value={newClient.phone} onChange={(e) => setNewClient({...newClient, phone: e.target.value})} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('clients.labels.ice', 'ICE')}</Form.Label>
              <Form.Control value={newClient.ice} onChange={(e) => setNewClient({...newClient, ice: e.target.value})} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('clients.labels.address', 'Adresse')}</Form.Label>
              <Form.Control as="textarea" rows={3} value={newClient.address} onChange={(e) => setNewClient({...newClient, address: e.target.value})} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>{t('clients.buttons.cancel', 'Annuler')}</Button>
          <Button variant="success" onClick={handleAdd}>{t('clients.buttons.save', 'Sauvegarder')}</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Client Modal */}
      <Modal show={showEdit} onHide={() => { setShowEdit(false); setEditingClient(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>{t('clients.editTitle', 'Modifier le Client')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>{t('clients.labels.name', 'Nom du client')}</Form.Label>
              <Form.Control required value={editingClient?.name || ''} onChange={(e) => setEditingClient({...editingClient, name: e.target.value})} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('clients.labels.company', 'Entreprise')}</Form.Label>
              <Form.Control value={editingClient?.company || ''} onChange={(e) => setEditingClient({...editingClient, company: e.target.value})} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('clients.labels.phone', 'Téléphone')}</Form.Label>
              <Form.Control value={editingClient?.phone || ''} onChange={(e) => setEditingClient({...editingClient, phone: e.target.value})} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('clients.labels.ice', 'ICE')}</Form.Label>
              <Form.Control value={editingClient?.ice || ''} onChange={(e) => setEditingClient({...editingClient, ice: e.target.value})} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('clients.labels.address', 'Adresse')}</Form.Label>
              <Form.Control as="textarea" rows={3} value={editingClient?.address || ''} onChange={(e) => setEditingClient({...editingClient, address: e.target.value})} />
            </Form.Group>
            {/* credit and status are intentionally hidden in the edit form per request */}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowEdit(false); setEditingClient(null); }}>{t('clients.buttons.cancel', 'Annuler')}</Button>
          <Button variant="success" onClick={handleUpdate}>{t('clients.buttons.update', 'Enregistrer')}</Button>
        </Modal.Footer>
      </Modal>
    </AnimationRevealPage>
  );
};

export default Clients;
